import React from 'react';
import PropTypes from 'prop-types';
import {
	Box,
	Flex,
	Link,
	HStack,
	Text,
	IconButton,
	Tooltip,
	Icon,
} from '@chakra-ui/react';
import { FaInstagram, FaFacebook, FaYoutube } from 'react-icons/fa';
import { FiShield } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

interface SocialButtonProps {
	icon: any;
	label: string;
	href: string;
}

const SocialButton = ({ icon, label, href }: SocialButtonProps) => {
	return (
		<Tooltip label={label} hasArrow placement='top'>
			<IconButton
				as='a'
				href={href}
				target='_blank'
				rel='noopener noreferrer'
				aria-label={label}
				icon={<Icon as={icon} boxSize='14px' />}
				size='sm'
				variant='ghost'
				borderRadius='lg'
				bg='gray.50'
				border='1px solid'
				borderColor='gray.200'
				color='gray.600'
				boxShadow='xs'
				transition='all 0.2s ease'
				_hover={{
					bg: 'teal.50',
					color: 'teal.600',
					borderColor: 'teal.200',
					transform: 'translateY(-1px)',
					boxShadow: 'sm',
				}}
				_active={{
					transform: 'translateY(0)',
				}}
			/>
		</Tooltip>
	);
};

SocialButton.propTypes = {
	icon: PropTypes.elementType.isRequired,
	label: PropTypes.string.isRequired,
	href: PropTypes.string.isRequired,
};

export const Footer = () => {
	const { t } = useTranslation();
	const currentYear = new Date().getFullYear();

	return (
		<Box
			as='footer'
			w='100%'
			bg='white'
			borderTop='1px solid'
			borderColor='gray.200'
			py={3.5}
			px={{ base: 4, md: 6 }}
			boxShadow='0 -1px 3px 0 rgba(0, 0, 0, 0.02)'
		>
			<Flex
				direction={{ base: 'column', md: 'row' }}
				align='center'
				justify='space-between'
				gap={{ base: 3, md: 2 }}
				w='100%'
			>
				{/* Left: Brand, Copyright & Powered by */}
				<HStack spacing={2} wrap='wrap' justify={{ base: 'center', md: 'flex-start' }}>
					<Text fontSize='xs' color='gray.600' fontWeight='medium'>
						&copy; {currentYear} <strong>Angels on Watch</strong> • {t('Teleasistencia 24/7') || 'Teleasistencia 24/7'}
					</Text>
					<Text as='span' color='gray.300' display={{ base: 'none', sm: 'inline' }}>
						•
					</Text>
					<Text fontSize='xs' color='gray.500'>
						{t('Powered by') || 'Powered by'}{' '}
						<Link
							href='https://angelsonwatch.com/es/collections/devices'
							isExternal
							color='teal.600'
							fontWeight='semibold'
							_hover={{
								color: 'teal.700',
								textDecoration: 'underline',
							}}
						>
							Angels Caring
						</Link>
					</Text>
				</HStack>

				{/* Center: System Status Beacon */}
				<HStack
					spacing={2}
					px={3}
					py={1}
					borderRadius='full'
					bg='gray.50'
					border='1px solid'
					borderColor='gray.200'
					display={{ base: 'none', lg: 'flex' }}
				>
					<Box
						boxSize='6px'
						borderRadius='full'
						bg='teal.500'
						animation='pulse 2s infinite'
					/>
					<HStack spacing={1} color='gray.600' fontSize='10px' fontFamily='mono' fontWeight='semibold'>
						<Icon as={FiShield} boxSize='11px' color='teal.600' />
						<Text as='span'>SISTEMA TELEMÉTRICO ACTIVO</Text>
					</HStack>
				</HStack>

				{/* Right: Social Networks & External Links */}
				<HStack spacing={2} justify={{ base: 'center', md: 'flex-end' }}>
					<SocialButton
						label='Facebook'
						href='https://www.facebook.com/people/Angels-OnWatch/pfbid0236pZaNckuQkjsNmNxRAyRjPNSm85EvC7ZrwrpAn8fY8hdtm5U4xHaCqb2rtqshyhl/'
						icon={FaFacebook}
					/>
					<SocialButton
						label='YouTube'
						href='https://www.youtube.com/channel/UCF6-suOU0vObL44lMwd7rCA'
						icon={FaYoutube}
					/>
					<SocialButton
						label='Instagram'
						href='https://www.instagram.com/angels_on_watch/'
						icon={FaInstagram}
					/>
				</HStack>
			</Flex>
		</Box>
	);
};

export default Footer;
